import { useState, useEffect } from 'react'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'

import Dialog from './Dialog'
import Select from './Select'
import InputField from './InputField'
import GridIconButton from './GridIconButton'
import SortableTableHead from './SortableTableHead'
import GridCircularProgress from './GridCircularProgress'
import { titlesImpostazioni } from '../settings/settings'
import { useFeedback } from '../hooks/feedback-hook'
import { rangeFun, formatBodyField } from '../hooks/utils-functions'
import {
  useGetAccoppiamentiQuery,
  useCreateAccoppiamentoMutation,
  useUpdateAccoppiamentoMutation,
  useDeleteAccoppiamentoMutation,
  useGetImpostazioniAlgoritmiQuery,
  useUpdateImpostazioniMutation,
} from '../hooks/api-slice'

const columnsAccoppiamenti = [
  { label: 'Giornata', sortable: false },
  { label: 'Casa', sortable: false },
  { label: 'Trasferta', sortable: false },
  { label: 'Opzioni', sortable: false },
]

const availableGironiDa = [4, 6, 8, 10]
const availableLegsNames = ['Andata', 'Andata/Ritorno', 'Andata/Ritorno/Andata']
const availableLegsNumbers = [1, 2, 3]

const DialogAccoppiamenti = ({ open, handleClose }) => {
  const [selectedGironiDa, setSelectedGironiDa] = useState(availableGironiDa[0])

  const { data: impostazioniAlgoritmi, isLoading: isLoadingI } =
    useGetImpostazioniAlgoritmiQuery()

  const { data: accoppiamenti, isLoading: isLoadingA } =
    useGetAccoppiamentiQuery(undefined, {
      selectFromResult: ({ data }) => ({
        data: data?.filter(a => a.gironiDa === selectedGironiDa),
      }),
    })

  const [createAccoppiamentoMutation] = useCreateAccoppiamentoMutation()
  const [updateAccoppiamentoMutation] = useUpdateAccoppiamentoMutation()
  const [deleteAccoppiamentoMutation] = useDeleteAccoppiamentoMutation()
  const [updateImpostazioniMutation] = useUpdateImpostazioniMutation()

  const { setFeedback } = useFeedback()

  const [editingAccoppiamento, setEditingAccoppiamento] = useState()
  const [addingAccoppiamento, setAddingAccoppiamento] = useState()
  const [newAccoppiamento, setNewAccoppiamento] = useState()

  const cSetNewAccoppiamento = (field, value) =>
    setNewAccoppiamento({
      ...newAccoppiamento,
      [field]: formatBodyField(value),
    })

  const clearNewAccoppiamento = () => {
    setEditingAccoppiamento()
    setAddingAccoppiamento()
    setNewAccoppiamento()
  }

  useEffect(() => {
    clearNewAccoppiamento()
  }, [open])

  const addNewAccoppiamento = async () => {
    const body = {
      gironiDa: selectedGironiDa,
      ...newAccoppiamento,
    }
    const result = await createAccoppiamentoMutation(body)
    if (!result.error) clearNewAccoppiamento()
  }

  const editAccoppiamento = async id => {
    const body = {
      gironiDa: selectedGironiDa,
      ...newAccoppiamento,
    }
    const result = await updateAccoppiamentoMutation({ id, ...body })
    if (!result.error) clearNewAccoppiamento()
  }

  const deleteAccoppiamento = async id => {
    await deleteAccoppiamentoMutation(id)
  }

  const updateImpostazioniLegs = async (gironiDa, legs) => {
    if (!gironiDa || !legs) {
      setFeedback('Mancano delle informazioni!')
      return
    }

    const body = {
      algoritmi: { ...impostazioniAlgoritmi, [`Legs${gironiDa}`]: legs },
    }
    await updateImpostazioniMutation(body)
  }

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      title={titlesImpostazioni.titleAccoppiamenti}
      maxWidth='md'
      stretchHeight
    >
      {((isLoadingI || isLoadingA) && <GridCircularProgress />) ||
        (!accoppiamenti && <h4>Accoppiamenti non trovati.</h4>) || (
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={12}>
              <p>
                Gli accoppiamenti servono all'algoritmo Gironi per cercare di
                soddisfare le richieste di abbinamento, alternanza e
                indisponibilità e servono all'algoritmo Calendari per generare
                le singole partite.
                <br />
                <br />
                Un accoppiamento è una partita tra due squadre, identificate
                dalla posizione che occupano nel girone, che avviene in una
                determinata giornata di gioco. Gli accoppiamenti possono essere
                definiti per gironi da 4, 6, 8 e 10 squadre.
                <br />
                <br />
                Gli accoppiamenti per gironi da 3, 5, 7 e 9 vengono presi da
                quelli dei gironi da 4, 6, 8 e 10 squadre eliminando le gare in
                eccesso. Per esempio, se nella prima giornata di un girone a 4
                squadre (con posizioni 1-2-3-4) sono definiti gli abbinamenti
                1-2 e 3-4, nel girone a 3 squadre (con posizioni 1-2-3) verrà
                preso solo l'abbinamento 1-2.
              </p>
            </Grid>
            <Grid container sx={{ width: '100%' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Select
                  label='Numero di squadre'
                  value={selectedGironiDa}
                  options={availableGironiDa}
                  onChange={setSelectedGironiDa}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Select
                  label='Fasi previste'
                  value={impostazioniAlgoritmi[`Legs${selectedGironiDa}`]}
                  options={availableLegsNumbers}
                  optionLabels={availableLegsNames}
                  onChange={i => updateImpostazioniLegs(selectedGironiDa, i)}
                />
              </Grid>
            </Grid>
            <Grid container alignItems='center' sx={{ width: '100%' }}>
              {(!!addingAccoppiamento && (
                <>
                  <Grid size={{ xs: 4, md: 2 }}>
                    <center>
                      <InputField
                        id='giornata'
                        value={newAccoppiamento?.giornata}
                        onChange={i => cSetNewAccoppiamento('giornata', i)}
                        autoFocus
                        label={columnsAccoppiamenti[0].label}
                        type='number'
                      />
                    </center>
                  </Grid>
                  <Grid size={{ xs: 8, md: 4 }}>
                    <center>
                      <Select
                        id='squadraCasa'
                        value={newAccoppiamento?.casa}
                        options={rangeFun(selectedGironiDa).filter(
                          i => i !== newAccoppiamento?.trasferta,
                        )}
                        label={columnsAccoppiamenti[1].label}
                        onChange={i => cSetNewAccoppiamento('casa', i)}
                      />
                    </center>
                  </Grid>
                  <Grid size={{ xs: 8, md: 4 }}>
                    <center>
                      <Select
                        id='squadraTrasferta'
                        value={newAccoppiamento?.trasferta}
                        options={rangeFun(selectedGironiDa).filter(
                          i => i !== newAccoppiamento?.casa,
                        )}
                        label={columnsAccoppiamenti[2].label}
                        onChange={i => cSetNewAccoppiamento('trasferta', i)}
                      />
                    </center>
                  </Grid>
                  <GridIconButton
                    type='save'
                    size={{ xs: 2, md: 1 }}
                    onClick={addNewAccoppiamento}
                  />
                  <GridIconButton
                    type='clear'
                    size={{ xs: 2, md: 1 }}
                    onClick={clearNewAccoppiamento}
                  />
                </>
              )) || (
                <GridIconButton
                  type='add'
                  size={12}
                  onClick={() => {
                    setEditingAccoppiamento()
                    setAddingAccoppiamento(true)
                    setNewAccoppiamento({})
                  }}
                  text='Nuovo accoppiamento'
                />
              )}
            </Grid>
            {(accoppiamenti.length === 0 && (
              <h4>Nessun accoppiamento trovato.</h4>
            )) || (
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={accoppiamenti}
                    columns={columnsAccoppiamenti}
                  />
                  <TableBody>
                    {impostazioniAlgoritmi[`Legs${selectedGironiDa}`] >= 1 && // Andata (Legs: 1, 2, 3)
                      accoppiamenti.map((a, index) => {
                        return (
                          <TableRow key={index}>
                            {(!!editingAccoppiamento &&
                              newAccoppiamento?._id === a.id && (
                                <>
                                  <TableCell padding='checkbox'>
                                    <InputField
                                      id='giornata'
                                      value={newAccoppiamento?.giornata}
                                      onChange={i =>
                                        cSetNewAccoppiamento('giornata', i)
                                      }
                                      autoFocus
                                      label={columnsAccoppiamenti[0].label}
                                      type='number'
                                      mode='update'
                                    />
                                  </TableCell>
                                  <TableCell padding='checkbox'>
                                    <Select
                                      id='squadraCasa'
                                      value={newAccoppiamento?.casa}
                                      options={rangeFun(
                                        selectedGironiDa,
                                      ).filter(
                                        i => i !== newAccoppiamento?.trasferta,
                                      )}
                                      label={columnsAccoppiamenti[1].label}
                                      onChange={i =>
                                        cSetNewAccoppiamento('casa', i)
                                      }
                                      mode='update'
                                    />
                                  </TableCell>
                                  <TableCell padding='checkbox'>
                                    <Select
                                      id='squadraTrasferta'
                                      value={newAccoppiamento?.trasferta}
                                      options={rangeFun(
                                        selectedGironiDa,
                                      ).filter(
                                        i => i !== newAccoppiamento?.casa,
                                      )}
                                      label={columnsAccoppiamenti[2].label}
                                      onChange={i =>
                                        cSetNewAccoppiamento('trasferta', i)
                                      }
                                      mode='update'
                                    />
                                  </TableCell>
                                  <TableCell padding='checkbox'>
                                    <Grid container sx={{ width: '100%' }}>
                                      <GridIconButton
                                        type='save'
                                        onClick={() => editAccoppiamento(a.id)}
                                      />
                                      <GridIconButton
                                        type='clear'
                                        onClick={clearNewAccoppiamento}
                                      />
                                    </Grid>
                                  </TableCell>
                                </>
                              )) || (
                              <>
                                <TableCell>{a.giornata}</TableCell>
                                <TableCell>{a.casa}</TableCell>
                                <TableCell>{a.trasferta}</TableCell>
                                <TableCell>
                                  <Grid container sx={{ width: '100%' }}>
                                    <GridIconButton
                                      type='edit'
                                      onClick={() => {
                                        setEditingAccoppiamento(true)
                                        setAddingAccoppiamento()
                                        setNewAccoppiamento(a)
                                      }}
                                    />
                                    <GridIconButton
                                      type='delete'
                                      onClick={() => deleteAccoppiamento(a.id)}
                                    />
                                  </Grid>
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        )
                      })}
                    {impostazioniAlgoritmi[`Legs${selectedGironiDa}`] >= 2 && // Ritorno (Legs: 2, 3)
                      accoppiamenti.map((a, index) => {
                        return (
                          <TableRow
                            key={index}
                            sx={{ borderTop: index === 0 ? '2px solid' : '' }}
                          >
                            <TableCell>
                              {selectedGironiDa -
                                (selectedGironiDa % 2 === 0 ? 1 : 0) +
                                a.giornata}
                            </TableCell>
                            <TableCell>{a.trasferta}</TableCell>
                            <TableCell>{a.casa}</TableCell>
                            <TableCell>
                              <Grid container sx={{ width: '100%' }}>
                                <GridIconButton transparent type='edit' />
                              </Grid>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    {impostazioniAlgoritmi[`Legs${selectedGironiDa}`] >= 3 && // Andata (Legs: 2, 3)
                      accoppiamenti.map((a, index) => {
                        return (
                          <TableRow
                            key={index}
                            sx={{ borderTop: index === 0 ? '2px solid' : '' }}
                          >
                            <TableCell>
                              {2 *
                                (selectedGironiDa -
                                  (selectedGironiDa % 2 === 0 ? 1 : 0)) +
                                a.giornata}
                            </TableCell>
                            <TableCell>{a.casa}</TableCell>
                            <TableCell>{a.trasferta}</TableCell>
                            <TableCell>
                              <Grid container sx={{ width: '100%' }}>
                                <GridIconButton transparent type='edit' />
                              </Grid>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        )}
    </Dialog>
  )
}

export default DialogAccoppiamenti
